import React, { useState } from 'react';
import AMPageShell from './components/AMPageShell';
import Icon from 'components/AppIcon';
import { ASSETS } from './data/assetData';

const VALUATION_STATUS_COLOURS = {
  'Not required': 'bg-muted text-muted-foreground',
  'Under review': 'bg-warning/10 text-warning',
  'Submitted': 'bg-primary/10 text-primary',
  'Closed': 'bg-success/10 text-success',
};

export default function ValuationView({ selectedAssets, showToast }) {
  const assets = selectedAssets || ASSETS;
  const [quarterlyToggle, setQuarterlyToggle] = useState(false);
  const [activeAssetId, setActiveAssetId] = useState(assets?.[0]?.id);
  const asset = assets?.find((a) => a?.id === activeAssetId) || assets?.[0];

  if (!asset) return <AMPageShell activeTab="/asset-management/valuation-view"><p className="text-muted-foreground font-caption">No assets selected.</p></AMPageShell>;

  return (
    <AMPageShell activeTab="/asset-management/valuation-view">
      <div className="flex gap-6">
        {/* Asset list */}
        <div className="w-48 flex-shrink-0">
          <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">Asset</p>
            </div>
            {assets?.map((a) => (
              <button key={a?.id} onClick={() => setActiveAssetId(a?.id)}
                className={`w-full text-left px-4 py-2.5 transition-colors ${
                  a?.id === activeAssetId ? 'bg-primary/5 border-l-2 border-primary' : 'hover:bg-muted/40'
                }`}>
                <p className="font-caption text-sm font-medium text-foreground truncate">{a?.name}</p>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-caption font-medium mt-1 inline-block ${VALUATION_STATUS_COLOURS?.[a?.valuationStatus] || 'bg-muted text-muted-foreground'}`}>
                  {a?.valuationStatus}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Valuation content */}
        <div className="flex-1 min-w-0 space-y-5">
          <div className="bg-card border border-border rounded-lg shadow-xs p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.2rem' }}>{asset?.name}</h2>
                <p className="font-caption text-xs text-muted-foreground">{asset?.sector} · {asset?.country}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2.5 py-1 rounded-full font-caption font-medium ${VALUATION_STATUS_COLOURS?.[asset?.valuationStatus] || 'bg-muted text-muted-foreground'}`}>
                  {asset?.valuationStatus}
                </span>
                <button
                  onClick={() => setQuarterlyToggle((p) => !p)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-caption font-medium border transition-all duration-250 ${
                    quarterlyToggle ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <Icon name="RefreshCw" size={12} color={quarterlyToggle ? 'white' : 'currentColor'} strokeWidth={2} />
                  Quarterly Review
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-muted/30 rounded-md p-4">
                <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Operational Facts Validated</p>
                <p className="font-body text-sm text-foreground leading-relaxed">{asset?.operationalFacts}</p>
              </div>
              <div className="bg-muted/30 rounded-md p-4">
                <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Assumptions Likely to Influence Valuation</p>
                <p className="font-body text-sm text-foreground leading-relaxed">{asset?.valuationAssumptions}</p>
              </div>
            </div>

            <div className="mt-5 p-4 border border-border rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="FileText" size={15} color="var(--color-muted-foreground)" strokeWidth={2} />
                <p className="font-caption text-sm font-semibold text-foreground">Valuation Committee Notes</p>
              </div>
              <p className="font-body text-sm text-muted-foreground">{asset?.valuationNotes}</p>
              <button className="mt-3 text-xs text-primary hover:underline font-caption flex items-center gap-1">
                <Icon name="ExternalLink" size={12} color="var(--color-primary)" strokeWidth={2} />
                Open valuation committee folder (placeholder)
              </button>
            </div>
          </div>

          {/* Portfolio summary table */}
          <div className="bg-card border border-border rounded-lg shadow-xs overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: '1rem' }}>Portfolio Valuation Status</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    {['Asset', 'Sector', 'Country', 'IRR', 'Multiple', 'Valuation Status']?.map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assets?.map((a) => (
                    <tr key={a?.id} className={`border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer transition-colors ${
                      a?.id === activeAssetId ? 'bg-primary/5' : ''
                    }`} onClick={() => setActiveAssetId(a?.id)}>
                      <td className="px-4 py-3 font-caption text-sm font-medium text-foreground">{a?.name}</td>
                      <td className="px-4 py-3 font-caption text-xs text-muted-foreground">{a?.sector}</td>
                      <td className="px-4 py-3 font-caption text-xs text-muted-foreground">{a?.country}</td>
                      <td className="px-4 py-3 font-data text-sm font-semibold text-foreground">{a?.irr}%</td>
                      <td className="px-4 py-3 font-data text-sm font-semibold text-foreground">{a?.multiple}x</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-medium ${VALUATION_STATUS_COLOURS?.[a?.valuationStatus] || 'bg-muted text-muted-foreground'}`}>
                          {a?.valuationStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AMPageShell>
  );
}
